/**
 * Document Persistence Manager
 * 
 * Based on RFC: partial-delta-sync.md
 * 
 * Handles periodic persistence of in-memory source of truth to SQLite database.
 * 
 * Key responsibilities:
 * - Persist pending properties from in-memory to SQLite
 * - Track last persistence time
 * - Batch persistence operations for efficiency
 * - Handle persistence errors gracefully
 */

import { InMemorySourceOfTruth, Property } from './InMemorySourceOfTruth';
import { SQLiteService } from './SQLiteService';

export interface PersistenceResult {
  propertiesPersisted: number;
  errors: string[];
  persistedAt: number;
}

export interface PersistenceOptions {
  batchSize?: number;
  retryAttempts?: number;
  retryDelayMs?: number;
}

export class DocumentPersistenceManager {
  private sourceOfTruth: InMemorySourceOfTruth;
  private sqliteService: SQLiteService;
  private documentId: string;
  private lastPersistenceTime: number = 0;
  private persistenceInProgress: boolean = false;

  constructor(
    sourceOfTruth: InMemorySourceOfTruth,
    sqliteService: SQLiteService,
    documentId: string
  ) {
    this.sourceOfTruth = sourceOfTruth;
    this.sqliteService = sqliteService;
    this.documentId = documentId;
  }

  /**
   * Persist all pending properties to SQLite database
   */
  async persistPendingProperties(options: PersistenceOptions = {}): Promise<PersistenceResult> {
    if (this.persistenceInProgress) {
      throw new Error('Persistence already in progress');
    }

    this.persistenceInProgress = true;
    const startTime = Date.now();
    
    try {
      const pendingProperties = this.sourceOfTruth.getPropertiesPendingPersistence();
      
      if (pendingProperties.length === 0) {
        return {
          propertiesPersisted: 0,
          errors: [],
          persistedAt: startTime
        };
      }

      
      
      const result = await this.persistPropertiesBatch(pendingProperties, options);
      
      if (result.errors.length === 0) {
        // Mark properties as persisted only if no errors
        this.sourceOfTruth.markPropertiesAsPersisted();
        this.lastPersistenceTime = startTime;
        
      } else {
        console.warn(`⚠️ [PERSISTENCE] Persisted ${result.propertiesPersisted} properties with ${result.errors.length} errors`);
      }

      return result;
    } finally {
      this.persistenceInProgress = false;
    }
  }

  /**
   * Get properties that have been added to memory since last persistence
   */
  getPropertiesSinceLastPersistence(): Property[] {
    return this.sourceOfTruth.getPropertiesSince(this.lastPersistenceTime);
  }

  /**
   * Check if persistence is needed
   */
  isPersistenceNeeded(): boolean {
    const pendingProperties = this.sourceOfTruth.getPropertiesPendingPersistence();
    return pendingProperties.length > 0;
  }

  /**
   * Get the last persistence time
   */
  getLastPersistenceTime(): number {
    return this.lastPersistenceTime;
  }

  /**
   * Check if persistence is currently in progress
   */
  isPersistenceInProgress(): boolean {
    return this.persistenceInProgress;
  }

  /**
   * Load properties from SQLite into in-memory source of truth
   * This is used during document initialization
   */
  /**
   * Load subtree properties for a specific page (text blocks and their children)
   */
  async loadSubtreePropertiesFromDisk(pageId: string): Promise<number> {
    
    try {
      // Get all objects that are children of this page (text blocks, etc.)
      const allObjects = await this.sqliteService.getObjects();
      
      // Show text blocks specifically
      const textBlocks = allObjects.filter(obj => obj.object_type === 'text_block');
      
      const subtreeObjectIds: string[] = [];
      
      // Find all objects that have this page as parent (direct children)
      // and recursively find their children too
      const findSubtreeObjects = (parentId: string) => {
        for (const obj of allObjects) {
          if (obj.parent_id === parentId && !subtreeObjectIds.includes(obj.object_id)) {
            subtreeObjectIds.push(obj.object_id);
            // Recursively find children of this object
            findSubtreeObjects(obj.object_id);
          }
        }
      };
      
      findSubtreeObjects(pageId);
      
      if (subtreeObjectIds.length === 0) {
        return 0;
      }
      
      
      // Load properties for all subtree objects
      const properties = await this.fetchPropertiesFromSQLite(subtreeObjectIds);
      
      
      // Add them to in-memory source of truth with 'local' source
      let loadedCount = 0;
      for (const property of properties) {
        this.sourceOfTruth.addProperty({
          ...property,
          source: 'local',
          addedToMemoryAt: Date.now()
        });
        loadedCount++;
      }
      
      
      // The UI should be updated through DocumentStateManagerRFC's subscription to InMemorySourceOfTruth
      // But InMemorySourceOfTruth doesn't have a built-in notification system, so we need to trigger a refresh
      
      return loadedCount;
    } catch (error) {
      console.error(`❌ [PERSISTENCE] Failed to load subtree properties for ${pageId}:`, error);
      throw error;
    }
  }

  async loadPropertiesFromDisk(objectIds?: string[]): Promise<number> {
    
    try {
      // Get all properties from SQLite
      const properties = await this.fetchPropertiesFromSQLite(objectIds);
      
      
      // Add them to in-memory source of truth with 'local' source
      let loadedCount = 0;
      for (const property of properties) {
        this.sourceOfTruth.addProperty({
          ...property,
          source: 'local',
          addedToMemoryAt: Date.now()
        });
        loadedCount++;
      }
      
      return loadedCount;
    } catch (error) {
      console.error(`❌ [PERSISTENCE] Failed to load properties from SQLite:`, error);
      throw error;
    }
  }

  // Private methods

  private async persistPropertiesBatch(
    properties: Property[],
    options: PersistenceOptions
  ): Promise<PersistenceResult> {
    const batchSize = options.batchSize || 100;
    const retryAttempts = options.retryAttempts || 3;
    
    const result: PersistenceResult = {
      propertiesPersisted: 0,
      errors: [],
      persistedAt: Date.now()
    };

    // Process properties in batches
    for (let i = 0; i < properties.length; i += batchSize) {
      const batch = properties.slice(i, i + batchSize);
      
      try {
        await this.persistBatchWithRetry(batch, retryAttempts, options.retryDelayMs || 100);
        result.propertiesPersisted += batch.length;
      } catch (error) {
        const errorMessage = `Failed to persist batch ${i}-${i + batch.length}: ${error}`;
        result.errors.push(errorMessage);
        console.error(`❌ [PERSISTENCE] ${errorMessage}`);
      }
    }

    return result;
  }

  private async persistBatchWithRetry(
    properties: Property[],
    maxRetries: number,
    retryDelayMs: number
  ): Promise<void> {
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        await this.persistBatchToSQLite(properties);
        return; // Success
      } catch (error) {
        lastError = error as Error;
        
        if (attempt < maxRetries) {
          console.warn(`⚠️ [PERSISTENCE] Attempt ${attempt + 1} failed, retrying in ${retryDelayMs}ms:`, error);
          await this.delay(retryDelayMs);
        }
      }
    }
    
    throw lastError;
  }

  private async persistBatchToSQLite(properties: Property[]): Promise<void> {
    // Convert properties to format expected by SQLiteService
    const sqliteProperties = properties.map(prop => ({
      object_id: prop.objectId,
      property: prop.propertyName, // Must match ObjectProperty interface
      value: typeof prop.value === 'object' && prop.value !== null 
        ? JSON.stringify(prop.value)  // Convert objects to JSON strings for SQLite
        : prop.value,                 // Keep primitives as-is
      lww_counter: prop.lwwCounter || 0,
      lww_random: prop.lwwRandom || 0,
      replica_id: prop.replicaId,
      logical_timestamp: prop.logicalTimestamp,
      updated_at: Date.now(),
      user_id: 'system'
    }));

    // Use SQLiteService to import properties
    await this.sqliteService.importProperties(sqliteProperties);
  }

  private async fetchPropertiesFromSQLite(objectIds?: string[]): Promise<Property[]> {
    // This is a simplified version - in practice, you might want to use
    // more specific queries based on objectIds or other criteria
    
    // For now, we'll use a basic approach to get all properties
    // In a real implementation, you'd want more efficient queries
    
    const allObjects = await this.sqliteService.getObjects();
    const properties: Property[] = [];
    
    for (const obj of allObjects) {
      if (objectIds && !objectIds.includes(obj.object_id)) {
        continue; // Skip if filtering by objectIds
      }
      
      const objectProperties = await this.sqliteService.getObjectProperties(obj.object_id);
      
      for (const prop of objectProperties) {
        properties.push({
          objectId: prop.object_id,
          propertyName: prop.property,
          value: prop.value,
          lwwCounter: prop.lww_counter, // Preserve null for special properties
          lwwRandom: prop.lww_random,   // Preserve null for special properties
          replicaId: prop.replica_id,
          logicalTimestamp: prop.logical_timestamp,
          source: 'local',
          addedToMemoryAt: 0 // Will be set by caller
        });
      }
    }
    
    // Sort properties to ensure object.metadata comes first for each object
    properties.sort((a, b) => {
      // First sort by object_id to group properties by object
      if (a.objectId !== b.objectId) {
        return a.objectId.localeCompare(b.objectId);
      }
      
      // Within the same object, ensure object.metadata comes first
      if (a.propertyName === 'object.metadata' && b.propertyName !== 'object.metadata') {
        return -1;
      }
      if (b.propertyName === 'object.metadata' && a.propertyName !== 'object.metadata') {
        return 1;
      }
      
      // For other properties, sort by property name for consistency
      return a.propertyName.localeCompare(b.propertyName);
    });
    
    return properties;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
